import { MAPBOX_TOKEN } from "@/lib/env";
import type { LngLat } from "@/utils/polyline";
import { poiKey, readCache, writeCache } from "@/utils/poiCache";

export interface PoiHit {
  id: string;
  name: string;
  category: string;
  center: LngLat;
}

/**
 * Allowed categories for POI search (cafes, restaurants, landmarks, shops).
 * Parks/water/road types come from OSM (cheaper, no quota).
 */
const ALLOWED = new Set(["cafe", "restaurant", "landmark", "monument", "shop", "shopping"]);

/**
 * POI search via Mapbox geocoding "category" endpoint.
 * Caches results in localStorage for 48h.
 * Returns [] without throwing on any failure so route scoring can proceed.
 */
export async function searchPoisNear(
  center: LngLat,
  category: string,
  radiusMeters = 500,
): Promise<PoiHit[]> {
  if (!MAPBOX_TOKEN) return [];
  if (!ALLOWED.has(category)) return [];

  const key = poiKey(category, center[1], center[0], radiusMeters);
  const cached = readCache<PoiHit[]>(key);
  if (cached) return cached;

  const dLat = radiusMeters / 111_000;
  const dLng = radiusMeters / (111_000 * Math.cos((center[1] * Math.PI) / 180));
  const bbox = [center[0] - dLng, center[1] - dLat, center[0] + dLng, center[1] + dLat].join(",");
  const url =
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(category)}.json` +
    `?proximity=${center[0]},${center[1]}&bbox=${bbox}&limit=10&access_token=${MAPBOX_TOKEN}`;
  try {
    const res = await fetch(url);
    if (!res.ok) return [];
    const data = await res.json();
    const hits: PoiHit[] = (data.features ?? []).map((f: any): PoiHit => ({
      id: f.id,
      name: f.text,
      category,
      center: f.center,
    }));
    writeCache(key, hits);
    return hits;
  } catch {
    return [];
  }
}

/**
 * Bounding-box POI search — useful for dense urban routes where one bbox
 * query replaces N proximity queries along the route.
 */
export async function searchPoisInBbox(
  bbox: { minLng: number; minLat: number; maxLng: number; maxLat: number },
  category: string,
  limit = 25,
): Promise<PoiHit[]> {
  if (!MAPBOX_TOKEN) return [];
  if (!ALLOWED.has(category)) return [];

  // SNAP THE BOX TO A GRID SO ALTERNATIVES SHARE ONE LOOKUP (Aug 20).
  // Every candidate route on a trip asks for cafes, restaurants and
  // landmarks in ITS OWN bounding box. Between alternatives between the
  // same two points those boxes differ by ~100 m - visually the same
  // corridor, but a different cache key every time, so a single search fired
  // up to 21 near-identical requests and cached none of them usefully.
  //
  // Snapping OUTWARD to a ~1.1 km grid makes near-identical boxes compute the
  // identical box, so the second and later routes hit the cache. Outward
  // matters: the snapped box is always a SUPERSET of the caller's, so nobody
  // loses coverage they would have had.
  //
  // Deliberately not the other obvious fix - one union box over all routes.
  // The result limit is per request, so a union box spanning every
  // alternative would spread the same 25 results over a much wider area and
  // quietly give each route FEWER of its own nearby places. Snapping keeps
  // each box essentially its own size.
  const GRID = 0.01;
  const snapped = {
    minLng: Math.floor(bbox.minLng / GRID) * GRID,
    minLat: Math.floor(bbox.minLat / GRID) * GRID,
    maxLng: Math.ceil(bbox.maxLng / GRID) * GRID,
    maxLat: Math.ceil(bbox.maxLat / GRID) * GRID,
  };
  const cx = (snapped.minLng + snapped.maxLng) / 2;
  const cy = (snapped.minLat + snapped.maxLat) / 2;
  // Approx radius for cache key
  const radius = Math.round(((snapped.maxLat - snapped.minLat) * 111_000) / 2);
  const key = poiKey(`bbox-${category}`, cy, cx, radius);
  const cached = readCache<PoiHit[]>(key);
  if (cached) return cached;

  const bboxStr = [snapped.minLng, snapped.minLat, snapped.maxLng, snapped.maxLat]
    .map((n) => n.toFixed(5))
    .join(",");
  const url =
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(category)}.json` +
    `?bbox=${bboxStr}&limit=${limit}&access_token=${MAPBOX_TOKEN}`;
  try {
    const res = await fetch(url);
    if (!res.ok) return [];
    const data = await res.json();
    const hits: PoiHit[] = (data.features ?? []).map((f: any): PoiHit => ({
      id: f.id,
      name: f.text,
      category,
      center: f.center,
    }));
    writeCache(key, hits);
    return hits;
  } catch {
    return [];
  }
}
